import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, PackageCheck, FlaskConical, ImagePlus, CheckCircle, XCircle, Truck, Plus, Play, Pause, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { fmtDT } from "@/lib/format";

const ACTIVITIES = ["sample_preparation", "product_collection", "packing", "image_video", "qc_coordination", "courier_coordination"];
const HOLD_REASONS = ["waiting_for_stock", "waiting_for_qc", "waiting_for_sales_approval", "waiting_for_customer_details", "other"];
const OPS = ["admin", "warehouse_manager", "supervisor", "worker", "dispatch"];
const QC_ROLES = ["admin", "warehouse_manager", "qc"];
const DSP = ["admin", "warehouse_manager", "supervisor", "dispatch"];
const fmtMin = (m) => (m == null ? "—" : m < 1 ? `${Math.max(1, Math.round(m * 60))} sec` : m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m` : `${Math.round(m * 10) / 10} min`);

function Card({ title, testId, children, action }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3 text-sm py-0.5">
      <span className="text-muted-foreground text-xs">{k}</span>
      <span className={`text-right ${mono ? "metric text-xs" : ""}`}>{v ?? "—"}</span>
    </div>
  );
}

export default function SampleDetail() {
  const { sid } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [packForm, setPackForm] = useState({ packed_quantity: "", packages: "", remarks: "" });
  const [qcRemarks, setQcRemarks] = useState("");
  const [saRemarks, setSaRemarks] = useState("");
  const [dsp, setDsp] = useState({ courier: "", awb: "", packages: "", remarks: "" });
  const [assign, setAssign] = useState({ activity: "sample_preparation", worker_id: "" });
  const [holdReason, setHoldReason] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/samples/${sid}`);
      setS(r.data);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed to load sample");
    }
  }, [sid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/office/directory").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const act = async (path, body, okMsg) => {
    try {
      const r = await api.post(`/samples/${sid}/${path}`, body ?? {});
      setS(r.data);
      if (okMsg) toast.success(okMsg);
      return r.data;
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
      return null;
    }
  };

  const can = (roles) => roles.includes(me?.role);
  const ship = s.ship_to || {};
  const isWorkerOf = (a) => me?.role === "admin" || a.worker.user_id === me?.user_id;

  const uploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/samples/${sid}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS(r.data);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed");
    }
    e.target.value = "";
  };

  const gates = [
    ["Sample packed", !!s.packing],
    ["Factory QC approved", s.qc?.status === "approved"],
    ["Image / video uploaded", (s.media || []).length > 0],
    ["Sales approved", s.sales_approval?.status === "approved"],
    ["Ship-To complete", ["company", "contact_person", "mobile", "line1", "city", "state", "country", "pin"].every((k) => ship[k])],
  ];

  return (
    <div className="space-y-4" data-testid="sample-detail-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/samples")} data-testid="sample-back"><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight metric" data-testid="sample-number">{s.sample_id}</h1>
        <StatusBadge status={s.status} />
        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${s.sample_type === "paid" ? "bg-signal-amber/15 text-signal-amber" : "bg-signal-green/15 text-signal-green"}`} data-testid="sample-type">{s.sample_type === "paid" ? "Paid Sample" : "Free / Unpaid"}</span>
        <div className="ml-auto flex gap-4 text-xs font-mono text-muted-foreground">
          <span data-testid="sample-labour-time"><Clock className="w-3.5 h-3.5 inline mr-1" />Labour: {fmtMin(s.total_worker_minutes)}</span>
          <span data-testid="sample-elapsed-time">End-to-end: {s.elapsed_minutes != null ? fmtMin(s.elapsed_minutes) : "in progress"}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Card title="Request" testId="sample-request-card">
          <KV k="Customer" v={s.customer_name} />
          <KV k="Requested By" v={s.requested_by?.name} />
          <KV k="Sample Owner" v={s.owner?.name ? `${s.owner.name}${s.owner.department ? ` · ${s.owner.department}` : ""}` : s.requested_by?.name} />
          <KV k="Packing Owner" v={s.packing_owner?.name ? `${s.packing_owner.name}${s.packing_owner.department ? ` · ${s.packing_owner.department}` : ""}` : "Not yet assigned"} />
          <KV k="Currently With" v={s.responsibility?.assigned_name || s.owner?.name || s.requested_by?.name} />
          <KV k="Request Date" v={fmtDT(s.created_at)} mono />
          <KV k="Contact" v={s.contact_person} />
          <KV k="Mobile" v={s.mobile} mono />
          {s.remarks && <KV k="Remarks" v={s.remarks} />}
        </Card>

        <Card title="Ship-To (dispatch destination)" testId="sample-shipto-card">
          <KV k="Company" v={ship.company} />
          <KV k="Contact Person" v={ship.contact_person} />
          <KV k="Mobile" v={ship.mobile} mono />
          <KV k="Address" v={[ship.line1, ship.line2, ship.area].filter(Boolean).join(", ") || "—"} />
          <KV k="City / State" v={[ship.city, ship.state].filter(Boolean).join(", ") || "—"} />
          <KV k="Country / PIN" v={[ship.country, ship.pin].filter(Boolean).join(" — ") || "—"} />
        </Card>
      </div>

      <Card title={`Products (${s.items.length})`} testId="sample-products-card">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left">
            {["SKU", "Product", "Qty", "Colour", "Bottle Weight", "Neck Size"].map((h) => (
              <th key={h} className="px-2 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.items.map((i, idx) => (
              <tr key={`${i.sku}-${idx}`} className="border-b border-border/50" data-testid={`sample-product-${idx}`}>
                <td className="px-2 py-2 font-mono text-xs text-primary">{i.sku}</td>
                <td className="px-2 py-2">{i.product_name}</td>
                <td className="px-2 py-2 metric">{i.quantity}</td>
                <td className="px-2 py-2 text-xs">{i.colour || "—"}</td>
                <td className="px-2 py-2 metric text-xs">{i.bottle_weight_g ? `${i.bottle_weight_g}g` : "—"}</td>
                <td className="px-2 py-2 metric text-xs">{i.neck_size_mm ? `${i.neck_size_mm}mm` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-3">
        <Card title="Packing" testId="sample-packing-card"
          action={can(OPS) && ["requested", "preparing", "qc_rejected", "changes_requested"].includes(s.status) && (
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="sample-pack-button"
              onClick={async () => { const r = await act("pack", { packed_quantity: packForm.packed_quantity ? parseInt(packForm.packed_quantity) : null, packages: packForm.packages ? parseInt(packForm.packages) : null, remarks: packForm.remarks || null }, "Sample packed"); if (r) setPackForm({ packed_quantity: "", packages: "", remarks: "" }); }}>
              <PackageCheck className="w-3.5 h-3.5 mr-1" /> Mark Packed
            </Button>)}>
          {s.packing ? (
            <>
              <KV k="Packed By" v={<span className="font-bold">{s.packing.by}</span>} />
              <KV k="Packed At" v={fmtDT(s.packing.at)} mono />
              <KV k="Packed Quantity" v={s.packing.packed_quantity} mono />
              <KV k="Packages" v={s.packing.packages} mono />
              {s.packing.remarks && <KV k="Remarks" v={s.packing.remarks} />}
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Not packed yet — the logged-in packer is recorded as Packed By.</p>
              {can(OPS) && ["requested", "preparing", "qc_rejected", "changes_requested"].includes(s.status) && (
                <div className="grid grid-cols-3 gap-2">
                  <Input type="number" placeholder={`Qty (default ${s.items.reduce((a, i) => a + i.quantity, 0)})`} className="h-8" data-testid="pack-qty" value={packForm.packed_quantity} onChange={(e) => setPackForm({ ...packForm, packed_quantity: e.target.value })} />
                  <Input type="number" placeholder="Packages" className="h-8" data-testid="pack-packages" value={packForm.packages} onChange={(e) => setPackForm({ ...packForm, packages: e.target.value })} />
                  <Input placeholder="Remarks" className="h-8" data-testid="pack-remarks" value={packForm.remarks} onChange={(e) => setPackForm({ ...packForm, remarks: e.target.value })} />
                </div>
              )}
            </div>
          )}
        </Card>

        <Card title="Factory QC" testId="sample-qc-card"
          action={can(QC_ROLES) && ["packed", "qc_rejected"].includes(s.status) && (
            <div className="flex gap-1">
              <Button size="sm" className="bg-signal-green/20 text-signal-green hover:bg-signal-green/30" data-testid="qc-approve-button" onClick={() => act("qc", { decision: "approved", remarks: qcRemarks || null }, "QC approved")}><CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve</Button>
              <Button size="sm" className="bg-signal-red/20 text-signal-red hover:bg-signal-red/30" data-testid="qc-reject-button" onClick={() => act("qc", { decision: "rejected", remarks: qcRemarks || null }, "QC rejected — back for correction")}><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
            </div>)}>
          {s.qc ? (
            <>
              <KV k="QC Status" v={s.qc.status === "approved" ? <span className="text-signal-green font-bold">Approved</span> : <span className="text-signal-red font-bold">Rejected</span>} />
              <KV k="Checked By" v={s.qc.by} />
              <KV k="Date" v={fmtDT(s.qc.at)} mono />
              {s.qc.remarks && <KV k="Remarks" v={s.qc.remarks} />}
            </>
          ) : <p className="text-sm text-muted-foreground">Pending — QC can act once the sample is packed.</p>}
          {can(QC_ROLES) && ["packed", "qc_rejected"].includes(s.status) && (
            <Input placeholder="QC remarks (optional)" className="h-8 mt-2" data-testid="qc-remarks" value={qcRemarks} onChange={(e) => setQcRemarks(e.target.value)} />
          )}
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Card title="Images / Videos" testId="sample-media-card"
          action={can([...OPS, "qc", "sales"]) && !["dispatched", "in_transit", "out_for_delivery", "delivered", "cancelled"].includes(s.status) && (
            <label className="cursor-pointer">
              <input type="file" accept="image/*,video/*" className="hidden" data-testid="media-upload-input" onChange={uploadMedia} />
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><ImagePlus className="w-3.5 h-3.5" /> Upload</span>
            </label>)}>
          {(s.media || []).length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {s.media.map((m, i) => (
                <a key={m.file_id || m.key || `${m.name}-${i}`} href={`${API}/samples/${sid}/media/${i}/download`} target="_blank" rel="noreferrer" className="border border-border rounded-md p-2 hover:border-primary/50 transition-colors" data-testid={`media-${i}`}>
                  {m.kind === "image" ? (
                    <img src={`${API}/samples/${sid}/media/${i}/download`} alt={m.name} className="w-full h-16 object-cover rounded" />
                  ) : <div className="h-16 flex items-center justify-center text-[10px] font-mono text-muted-foreground">VIDEO</div>}
                  <p className="text-[9px] font-mono text-muted-foreground mt-1 truncate">{m.name}</p>
                  <p className="text-[9px] text-muted-foreground">{m.uploaded_by} · {fmtDT(m.at)}</p>
                </a>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No media uploaded yet.</p>}
        </Card>

        <Card title="Sales Approval" testId="sample-approval-card"
          action={can(["admin", "sales"]) && s.status === "pending_sales_approval" && (
            <div className="flex gap-1">
              <Button size="sm" className="bg-signal-green/20 text-signal-green hover:bg-signal-green/30" data-testid="sales-approve-button" onClick={() => act("sales-approve", { action: "approve", remarks: saRemarks || null }, "Approved for dispatch")}><Check className="w-3.5 h-3.5 mr-1" /> Approve for Dispatch</Button>
              <Button size="sm" className="bg-signal-amber/20 text-signal-amber hover:bg-signal-amber/30" data-testid="sales-changes-button" onClick={() => act("sales-approve", { action: "changes", remarks: saRemarks || null }, "Changes requested")}>Request Changes</Button>
            </div>)}>
          {s.sales_approval ? (
            <>
              <KV k="Status" v={s.sales_approval.status === "approved" ? <span className="text-signal-green font-bold">Approved for Dispatch</span> : <span className="text-signal-amber font-bold">Changes Requested</span>} />
              <KV k="By" v={s.sales_approval.by} />
              <KV k="Date" v={fmtDT(s.sales_approval.at)} mono />
              {s.sales_approval.remarks && <KV k="Remarks" v={s.sales_approval.remarks} />}
            </>
          ) : <p className="text-sm text-muted-foreground">Pending — needs QC approval + media first.</p>}
          {can(["admin", "sales"]) && s.status === "pending_sales_approval" && (
            <Input placeholder="Remarks (optional)" className="h-8 mt-2" data-testid="sales-approval-remarks" value={saRemarks} onChange={(e) => setSaRemarks(e.target.value)} />
          )}
        </Card>
      </div>

      <Card title="Dispatch & Tracking" testId="sample-dispatch-card"
        action={can(DSP) && s.status === "approved_for_dispatch" && (
          <Button size="sm" className="bg-signal-orange hover:bg-signal-orange/90 text-white" data-testid="sample-dispatch-button"
            onClick={async () => { const r = await act("dispatch", { courier: dsp.courier, awb: dsp.awb, packages: dsp.packages ? parseInt(dsp.packages) : null, remarks: dsp.remarks || null }, "Sample dispatched — inventory reduced"); if (r) setDsp({ courier: "", awb: "", packages: "", remarks: "" }); }}>
            <Truck className="w-3.5 h-3.5 mr-1" /> Dispatch Sample
          </Button>)}>
        {!s.dispatch && s.status === "approved_for_dispatch" && can(DSP) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <Input placeholder="Courier / Transporter *" className="h-9" data-testid="dispatch-courier" value={dsp.courier} onChange={(e) => setDsp({ ...dsp, courier: e.target.value })} />
            <Input placeholder="AWB / Tracking No *" className="h-9 font-mono" data-testid="dispatch-awb" value={dsp.awb} onChange={(e) => setDsp({ ...dsp, awb: e.target.value })} />
            <Input type="number" placeholder="Packages" className="h-9" data-testid="dispatch-packages" value={dsp.packages} onChange={(e) => setDsp({ ...dsp, packages: e.target.value })} />
            <Input placeholder="Remarks" className="h-9" data-testid="dispatch-remarks" value={dsp.remarks} onChange={(e) => setDsp({ ...dsp, remarks: e.target.value })} />
          </div>
        )}
        {!s.dispatch && (
          <div className="space-y-1" data-testid="dispatch-gates">
            {gates.map(([label, ok]) => (
              <p key={label} className={`text-xs font-mono flex items-center gap-2 ${ok ? "text-signal-green" : "text-signal-red"}`}>
                {ok ? <Check className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {label}
              </p>
            ))}
            {!gates.every(([, ok]) => ok) && <p className="text-xs text-signal-amber mt-1">Sample cannot be dispatched until all required steps are completed.</p>}
          </div>
        )}
        {s.dispatch && (
          <>
            <div className="grid grid-cols-2 gap-x-6">
              <KV k="Courier" v={s.dispatch.courier} />
              <KV k="AWB / Tracking" v={<span className="font-mono font-bold">{s.dispatch.awb}</span>} />
              <KV k="Dispatch Date" v={fmtDT(s.dispatch.at)} mono />
              <KV k="Packages" v={s.dispatch.packages} mono />
              <KV k="Tracking Status" v={<StatusBadge status={s.dispatch.tracking_status} />} />
              <KV k="Delivered" v={s.dispatch.delivered_at ? fmtDT(s.dispatch.delivered_at) : "—"} mono />
            </div>
            {can([...DSP, "sales"]) && !["delivered", "delivery_failed"].includes(s.dispatch.tracking_status) && (
              <div className="flex gap-1 mt-3 flex-wrap">
                {[["in_transit", "In Transit"], ["out_for_delivery", "Out for Delivery"], ["delivered", "Delivered"], ["delivery_failed", "Delivery Failed"]].map(([v, label]) => (
                  <Button key={v} size="sm" variant="outline" data-testid={`tracking-${v}`} onClick={() => act("tracking", { status: v }, `Marked ${label}`)}>{label}</Button>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="Worker Activities & Time Tracking" testId="sample-activities-card"
        action={can(["admin", "warehouse_manager", "supervisor", "sales"]) && !["dispatched", "in_transit", "out_for_delivery", "delivered", "cancelled"].includes(s.status) && (
          <div className="flex flex-wrap gap-1">
            <Select value={assign.activity} onValueChange={(v) => setAssign({ ...assign, activity: v })}>
              <SelectTrigger className="w-44 h-8" data-testid="assign-activity"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTIVITIES.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={assign.worker_id || "none"} onValueChange={(v) => setAssign({ ...assign, worker_id: v === "none" ? "" : v })}>
              <SelectTrigger className="w-36 h-8" data-testid="assign-worker"><SelectValue placeholder="Worker" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Worker —</SelectItem>
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" disabled={!assign.worker_id} data-testid="assign-button"
              onClick={async () => { const r = await act("activities", assign, "Activity assigned"); if (r) setAssign({ ...assign, worker_id: "" }); }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Assign
            </Button>
          </div>)}>
        {(s.activities || []).length > 0 ? (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">
              {["Activity", "Worker", "Status", "Started", "Completed", "Time Taken", ""].map((h) => (
                <th key={h} className="px-2 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {s.activities.map((a) => (
                <tr key={a.activity_id} className="border-b border-border/50" data-testid={`activity-${a.activity_id}`}>
                  <td className="px-2 py-2 capitalize">{a.activity.replace(/_/g, " ")}</td>
                  <td className="px-2 py-2 font-medium">{a.worker.name}</td>
                  <td className="px-2 py-2">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${a.status === "completed" ? "bg-signal-green/15 text-signal-green" : a.status === "in_progress" ? "bg-signal-blue/15 text-signal-blue" : a.status === "on_hold" ? "bg-signal-amber/15 text-signal-amber" : "bg-muted text-muted-foreground"}`}>
                      {a.status.replace(/_/g, " ")}{a.hold_reason ? ` — ${a.hold_reason.replace(/_/g, " ")}` : ""}
                    </span>
                  </td>
                  <td className="px-2 py-2 metric text-xs">{a.started_at ? fmtDT(a.started_at) : "—"}</td>
                  <td className="px-2 py-2 metric text-xs">{a.completed_at ? fmtDT(a.completed_at) : "—"}</td>
                  <td className="px-2 py-2 metric text-xs font-bold">{a.minutes != null ? fmtMin(a.minutes) : "—"}</td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {isWorkerOf(a) && ["pending", "on_hold"].includes(a.status) && (
                      <Button size="sm" variant="outline" className="h-7" data-testid={`activity-start-${a.activity_id}`} onClick={() => act(`activities/${a.activity_id}/start`, {})}><Play className="w-3 h-3 mr-1" /> Start</Button>
                    )}
                    {isWorkerOf(a) && a.status === "in_progress" && (
                      <div className="flex gap-1">
                        <Select value={holdReason[a.activity_id] || "none"} onValueChange={(v) => setHoldReason({ ...holdReason, [a.activity_id]: v === "none" ? "" : v })}>
                          <SelectTrigger className="w-36 h-7 text-xs" data-testid={`activity-hold-reason-${a.activity_id}`}><SelectValue placeholder="Hold reason" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Hold reason…</SelectItem>
                            {HOLD_REASONS.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" className="h-7" disabled={!holdReason[a.activity_id]} data-testid={`activity-hold-${a.activity_id}`} onClick={() => act(`activities/${a.activity_id}/hold`, { reason: holdReason[a.activity_id] })}><Pause className="w-3 h-3 mr-1" /> Hold</Button>
                        <Button size="sm" className="h-7 bg-signal-green/20 text-signal-green hover:bg-signal-green/30" data-testid={`activity-complete-${a.activity_id}`} onClick={() => act(`activities/${a.activity_id}/complete`, {})}><Check className="w-3 h-3 mr-1" /> Complete</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : <p className="text-sm text-muted-foreground">No worker activities assigned yet.</p>}
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-3">
          Total labour time: {fmtMin(s.total_worker_minutes)} · End-to-end (request → dispatch): {s.elapsed_minutes != null ? fmtMin(s.elapsed_minutes) : "in progress"}
        </p>
      </Card>

      <Card title="Activity Timeline" testId="sample-timeline-card">
        <div className="space-y-2">
          {[...(s.timeline || [])].reverse().map((t, i) => (
            <div key={`${t.at}-${i}`} className="flex gap-3 text-sm" data-testid={`timeline-${i}`}>
              <span className="metric text-xs text-muted-foreground whitespace-nowrap w-32 shrink-0">{fmtDT(t.at)}</span>
              <div>
                <span>{t.event}</span>
                <span className="text-xs text-muted-foreground"> — {t.by}{t.detail ? ` · ${t.detail}` : ""}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {can(["admin", "warehouse_manager", "sales"]) && !["dispatched", "in_transit", "out_for_delivery", "delivered", "cancelled"].includes(s.status) && (
        <Button variant="outline" className="border-signal-red/40 text-signal-red" data-testid="sample-cancel-button"
          onClick={async () => { if (window.confirm("Cancel this sample request?")) { await act("cancel", {}, "Sample cancelled"); } }}>
          Cancel Sample
        </Button>
      )}
    </div>
  );
}
